import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Loader2, Users, Check, AlertCircle, RefreshCw, Image } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/hooks/useAuth';
import { useIsAdmin } from '@/hooks/useIsAdmin';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { fixHarryPotterCover } from '@/utils/fixBadBookCover';

interface AuthorVariation {
  original: string;
  bookCount: number;
}

interface AuthorGroup {
  normalizedName: string;
  variations: AuthorVariation[];
  totalBooks: number;
}

export default function AdminDataCleanup() {
  const { user } = useAuth();
  const { isAdmin, loading: adminLoading } = useIsAdmin();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  const [loading, setLoading] = useState(false);
  const [duplicates, setDuplicates] = useState<AuthorGroup[]>([]);
  const [selectedCanonical, setSelectedCanonical] = useState<Record<string, string>>({});
  const [merging, setMerging] = useState<string | null>(null);
  const [fixingCover, setFixingCover] = useState(false);

  useEffect(() => {
    if (!adminLoading && !isAdmin) {
      navigate('/');
    }
  }, [isAdmin, adminLoading, navigate]);

  useEffect(() => {
    if (isAdmin) {
      findDuplicates();
    }
  }, [isAdmin]);

  const findDuplicates = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('admin-author-cleanup', {
        body: { action: 'find_duplicates' }
      });

      if (error) throw error;

      setDuplicates(data.duplicates || []);
      
      // Auto-select the most common variation as default
      const defaults: Record<string, string> = {};
      data.duplicates?.forEach((group: AuthorGroup) => {
        defaults[group.normalizedName] = group.variations[0].original;
      });
      setSelectedCanonical(defaults);

      toast({
        title: "Scan Complete",
        description: `Found ${data.duplicates?.length || 0} author groups with duplicate variations`,
      });
    } catch (error) {
      console.error('Error finding duplicates:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to scan for duplicate authors",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleMerge = async (group: AuthorGroup) => {
    const canonical = selectedCanonical[group.normalizedName];
    if (!canonical) return;

    const duplicateNames = group.variations
      .map(v => v.original)
      .filter(name => name !== canonical);

    if (duplicateNames.length === 0) return;

    setMerging(group.normalizedName);
    try {
      const { data, error } = await supabase.functions.invoke('admin-author-cleanup', {
        body: {
          action: 'merge_authors',
          canonicalName: canonical,
          duplicateNames
        }
      });

      if (error) throw error;

      toast({
        title: "Authors Merged",
        description: data.message,
      });

      // Remove this group from the list
      setDuplicates(prev => prev.filter(g => g.normalizedName !== group.normalizedName));
    } catch (error) {
      console.error('Error merging authors:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to merge authors",
      });
    } finally {
      setMerging(null);
    }
  };

  const handleFixHarryPotterCover = async () => {
    setFixingCover(true);
    try {
      await fixHarryPotterCover();
      toast({
        title: "Cover Fixed",
        description: "Harry Potter Deathly Hallows cover has been updated",
      });
    } catch (error) {
      console.error('Error fixing cover:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to fix book cover",
      });
    } finally {
      setFixingCover(false);
    }
  };

  if (adminLoading || !isAdmin) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Admin Header */}
      <header className="sticky top-[var(--header-height)] z-40 bg-card border-b border-border">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Button>
          <h1 className="text-xl font-heading font-semibold">Admin Tools</h1>
          <div className="flex items-center gap-2">
            <Button
              variant={location.pathname === '/admin/enrichment' ? 'default' : 'ghost'}
              onClick={() => navigate('/admin/enrichment')}
              size="sm"
            >
              Enrichment
            </Button>
            <Button
              variant={location.pathname === '/admin/data-cleanup' ? 'default' : 'ghost'}
              onClick={() => navigate('/admin/data-cleanup')}
              size="sm"
            >
              Data Cleanup
            </Button>
            <Button
              variant={location.pathname === '/admin/moderation' ? 'default' : 'ghost'}
              onClick={() => navigate('/admin/moderation')}
              size="sm"
            >
              Moderation
            </Button>
            <Button
              variant={location.pathname === '/admin/awards' ? 'default' : 'ghost'}
              onClick={() => navigate('/admin/awards')}
              size="sm"
            >
              Awards
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 space-y-6">
        <Card className="card-elevated">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Image className="w-5 h-5" />
              Fix Bad Book Covers
            </CardTitle>
            <CardDescription>
              Fix books with incorrect or broken cover images
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4">
              <Button
                onClick={handleFixHarryPotterCover}
                disabled={fixingCover}
              >
                {fixingCover ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Fixing...
                  </>
                ) : (
                  'Fix Harry Potter Cover'
                )}
              </Button>
              <span className="text-sm text-muted-foreground">
                Updates Deathly Hallows with correct OpenLibrary cover
              </span>
            </div>
          </CardContent>
        </Card>

        <Card className="card-elevated">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Author Name Deduplication
            </CardTitle>
            <CardDescription>
              Find and merge duplicate author entries caused by formatting variations (e.g., "R.F. Kuang" vs "R. F. Kuang")
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4">
              <Button
                onClick={findDuplicates}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Scanning...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Scan for Duplicates
                  </>
                )}
              </Button>
              
              {duplicates.length > 0 && (
                <Badge variant="secondary">
                  {duplicates.length} group{duplicates.length !== 1 ? 's' : ''} found
                </Badge>
              )}
            </div>

            {duplicates.length === 0 && !loading && (
              <Alert>
                <Check className="h-4 w-4" />
                <AlertDescription>
                  No duplicate author variations found! Your database is clean.
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        {duplicates.length > 0 && (
          <div className="space-y-4">
            {duplicates.map((group) => (
              <Card key={group.normalizedName} className="card-elevated">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg">
                        {group.variations.length} variations found
                      </CardTitle>
                      <CardDescription>
                        {group.totalBooks} book{group.totalBooks !== 1 ? 's' : ''} total
                      </CardDescription>
                    </div>
                    <Button
                      onClick={() => handleMerge(group)}
                      disabled={merging === group.normalizedName || !selectedCanonical[group.normalizedName]}
                      size="sm"
                    >
                      {merging === group.normalizedName ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Merging...
                        </>
                      ) : (
                        'Merge Authors'
                      )}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <RadioGroup
                    value={selectedCanonical[group.normalizedName] || ''}
                    onValueChange={(value) => {
                      setSelectedCanonical(prev => ({
                        ...prev,
                        [group.normalizedName]: value
                      }));
                    }}
                    className="space-y-3"
                  >
                    {group.variations.map((variation) => (
                      <div
                        key={variation.original}
                        className="flex items-center space-x-3 border rounded-lg p-3 hover:bg-accent/50 transition-colors"
                      >
                        <RadioGroupItem
                          value={variation.original}
                          id={`${group.normalizedName}-${variation.original}`}
                        />
                        <Label
                          htmlFor={`${group.normalizedName}-${variation.original}`}
                          className="flex-1 flex items-center justify-between cursor-pointer"
                        >
                          <span className="font-medium">{variation.original}</span>
                          <Badge variant="outline">
                            {variation.bookCount} book{variation.bookCount !== 1 ? 's' : ''}
                          </Badge>
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>

                  <Alert className="mt-4">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      Select the canonical (preferred) version above. All other variations will be merged into it.
                    </AlertDescription>
                  </Alert>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
